<template>
    <EDCHeader />
    <EDCHead title="Detalles de negocio" />

    <div class="b-details-page">
        <div class="b-details__hero" v-if="info.image">
            <div class="b-details__hero-bk">
                <img :src="info.image" :alt="info.name || ''" />
            </div>
        </div>

        <div class="b-details__intro" :class="{ 'b-details__intro--no-hero': !info.image }">
            <div class="b-details__intro-inner">
                <span class="b-details__badge">{{ getBusinessType(info.type) }}</span>
                <h1 class="b-details__title">{{ info.name }}</h1>
                <div class="b-details__rating-actions-row">
                    <div class="b-details__rating-row">
                        <div class="b-details__rating-inline" v-show="info.rating > 0">
                            <EDCStars :quantity="info.rating" :star_size="22" golden />
                            <span class="b-details__rating-num">{{ info.rating }}</span>
                        </div>
                        <p class="b-details__rating-empty" v-show="info.rating <= 0" role="status">
                            <svg class="b-details__rating-empty-ico" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            Sé el primero en calificar
                        </p>
                    </div>
                    <div class="b-details__actions">
                    <a
                        v-if="primaryWhatsappUrl"
                        :href="primaryWhatsappUrl"
                        class="b-details__btn b-details__btn--whatsapp"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <svg class="b-details__btn-ico" viewBox="0 0 24 24" aria-hidden="true" width="22" height="22"><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                        Enviar WhatsApp
                    </a>
                    <button type="button" class="b-details__btn b-details__btn--primary" @click="onCalificarClick">
                        <svg class="b-details__btn-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" width="22" height="22"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                        Calificar
                    </button>
                    <a
                        v-if="googleMapsUrl"
                        :href="googleMapsUrl"
                        class="b-details__btn b-details__btn--outline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <svg class="b-details__btn-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" width="22" height="22"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        Ver en Google Maps
                    </a>
                    </div>
                </div>
            </div>
        </div>

        <nav class="b-details__tabs" aria-label="Secciones del negocio">
            <div class="b-details__tabs-inner">
                <button
                    type="button"
                    class="b-details__tab b-details__tab--info"
                    :class="{ 'is-active': activeTab === 'info' }"
                    @click="setTab('info')"
                >
                    Información
                </button>
                <button
                    type="button"
                    class="b-details__tab b-details__tab--services"
                    :class="{ 'is-active': activeTab === 'services' }"
                    @click="setTab('services')"
                >
                    Servicio
                </button>
                <button
                    type="button"
                    class="b-details__tab b-details__tab--ratings"
                    :class="{ 'is-active': activeTab === 'ratings' }"
                    @click="setTab('ratings')"
                >
                    Calificación
                </button>
            </div>
        </nav>

        <div class="b-details__panel" v-show="activeTab === 'info'">
            <div class="b-details__panel-inner">
                <div class="b-details__gallery-wrap" v-if="info.images?.length">
                    <div class="b-images-splide__container">
                        <section class="splide b-details-splide" id="images-splide">
                            <div class="splide__track">
                                <ul class="splide__list" id="gallery">
                                    <li
                                        v-for="(slide, galleryIndex) in info.images"
                                        :key="slide.id"
                                        class="splide__slide b-image-slide"
                                    >
                                        <a
                                            :href="slide.file"
                                            class="b-image-slide__link"
                                            :data-pswp-width="slide.width || 1600"
                                            :data-pswp-height="slide.height || 1067"
                                            @click.stop.prevent="openGalleryAt(galleryIndex)"
                                        >
                                            <img :src="slide.file" :alt="info.name || 'Galería'" />
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </section>
                    </div>
                </div>

                <div class="b-details__grid">
                    <div class="b-details__grid-main">
                        <article class="b-details__card b-details__card--featured b-details__card--featured-loc">
                            <h2 class="b-details__card-title b-details__card-title--with-icon">
                                <svg class="b-details__card-title-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="22" height="22">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                <span>Ubicación</span>
                            </h2>
                            <p class="b-details__card-sub">Dónde encontrarnos en el departamento.</p>
                            <p class="b-details__text" v-if="info.address">{{ info.address }}</p>
                            <p class="b-details__municipio" v-if="info.municipio">{{ info.municipio }}</p>
                        </article>

                        <article class="b-details__card b-details__card--featured b-details__card--featured-about" v-if="info.description || hasBusinessAmenities">
                            <h2 class="b-details__card-title b-details__card-title--with-icon">
                                <svg class="b-details__card-title-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="22" height="22">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                <span>Sobre el lugar</span>
                            </h2>
                            <p class="b-details__description" v-if="info.description" v-html="businessDescriptionHtml"></p>
                            <div class="b-details__chips" v-if="hasBusinessAmenities">
                                <span v-if="info.wifi" class="b-details__chip">Wi‑Fi</span>
                                <span v-if="info.ac" class="b-details__chip">Aire acondicionado</span>
                                <span v-if="info.mobile_network" class="b-details__chip">Red móvil</span>
                                <span v-if="info.parking_service" class="b-details__chip">Parqueadero</span>
                                <span v-if="info.food_service" class="b-details__chip">Comidas</span>
                            </div>
                        </article>

                        <article class="b-details__card b-details__card--featured b-details__card--featured-contact">
                            <h2 class="b-details__card-title b-details__card-title--with-icon">
                                <svg class="b-details__card-title-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="22" height="22">
                                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                                </svg>
                                <span>Contacto</span>
                            </h2>
                            <p class="b-details__card-sub">Llama, escribe o visita nuestras redes.</p>
                            <div
                                class="b-details__contact-cols"
                                :class="{
                                    'b-details__contact-cols--two':
                                        (info.email_main || info.email_secondary) && businessPhoneRows.length,
                                }"
                                v-if="(info.email_main || info.email_secondary) || businessPhoneRows.length"
                            >
                                <div class="b-details__contact-actions" v-if="info.email_main || info.email_secondary">
                                    <a
                                        v-if="info.email_main"
                                        class="b-details__contact-btn b-details__contact-btn--mail"
                                        :href="'mailto:' + info.email_main"
                                    >
                                        <svg class="b-details__contact-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                                        {{ info.email_main }}
                                    </a>
                                    <a
                                        v-if="info.email_secondary"
                                        class="b-details__contact-btn b-details__contact-btn--mail"
                                        :href="'mailto:' + info.email_secondary"
                                    >
                                        <svg class="b-details__contact-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                                        {{ info.email_secondary }}
                                    </a>
                                </div>
                                <div class="b-details__contact-actions b-details__contact-actions--phones" v-if="businessPhoneRows.length">
                                    <a
                                        v-for="(row, idx) in businessPhoneRows"
                                        :key="'tel-' + idx"
                                        class="b-details__contact-btn"
                                        v-bind="row.attrs"
                                        :aria-label="row.ariaLabel"
                                    >
                                        <svg v-if="row.whatsapp" class="b-details__contact-ico" viewBox="0 0 24 24" aria-hidden="true" width="20" height="20"><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                                        <svg v-else class="b-details__contact-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" width="20" height="20"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                                        {{ row.tel }}
                                    </a>
                                </div>
                            </div>
                            <div class="b-details__social-row" v-if="info.facebook || info.instagram || info.x_twitter || info.tiktok || info.whatsapp">
                                <a v-show="info.whatsapp" class="b-details__social-item" :href="infoWhatsappSocialUrl" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                    <img src="../assets/social-media/phone.svg" alt="" />
                                </a>
                                <a v-show="info.facebook" class="b-details__social-item" :href="info.facebook" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                                    <img src="../assets/social-media/facebook.svg" alt="" />
                                </a>
                                <a v-show="info.x_twitter" class="b-details__social-item" :href="info.x_twitter" target="_blank" rel="noopener noreferrer" aria-label="X / Twitter">
                                    <img src="../assets/social-media/twitter.svg" alt="" />
                                </a>
                                <a v-show="info.instagram" class="b-details__social-item" :href="info.instagram" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                    <img src="../assets/social-media/instagram.svg" alt="" />
                                </a>
                                <a v-show="info.tiktok" class="b-details__social-item" :href="info.tiktok" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                                    <img src="../assets/social-media/tiktok.svg" alt="" />
                                </a>
                            </div>
                        </article>

                        <article
                            class="b-details__card b-details__card--featured b-details__card--featured-documents"
                            v-show="info.attachments?.length"
                        >
                            <h2 class="b-details__card-title b-details__card-title--with-icon">
                                <svg class="b-details__card-title-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="22" height="22">
                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <line x1="10" y1="9" x2="8" y2="9" />
                                </svg>
                                <span>Documentos</span>
                            </h2>
                            <p class="b-details__card-sub">Archivos del establecimiento para consultar o descargar (cartas, folletos y más).</p>
                            <div class="b-details__file-list">
                                <div class="b-details__file-item" v-for="item in info.attachments" :key="item.id">
                                    <div class="b-details__file-item-content">
                                        <p class="b-details__file-name">{{ item.name }}</p>
                                    </div>
                                    <a
                                        class="b-details__download-file"
                                        :href="attachmentFileUrl(item)"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Descargar documento"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
                                            <path fill="currentColor" d="M15 19.4688C14.8333 19.4688 14.6771 19.4429 14.5313 19.3913C14.3854 19.3396 14.25 19.2508 14.125 19.125L9.625 14.625C9.375 14.375 9.255 14.0833 9.265 13.75C9.275 13.4167 9.395 13.125 9.625 12.875C9.875 12.625 10.1721 12.495 10.5163 12.485C10.8604 12.475 11.1571 12.5946 11.4063 12.8438L13.75 15.1875V6.25C13.75 5.89584 13.87 5.59875 14.11 5.35875C14.35 5.11875 14.6467 4.99917 15 5C15.3542 5 15.6513 5.12 15.8913 5.36C16.1313 5.6 16.2508 5.89667 16.25 6.25V15.1875L18.5938 12.8438C18.8438 12.5938 19.1408 12.4738 19.485 12.4838C19.8292 12.4938 20.1258 12.6242 20.375 12.875C20.6042 13.125 20.7242 13.4167 20.735 13.75C20.7458 14.0833 20.6258 14.375 20.375 14.625L15.875 19.125C15.75 19.25 15.6146 19.3388 15.4688 19.3913C15.3229 19.4438 15.1667 19.4696 15 19.4688ZM7.5 25C6.8125 25 6.22375 24.755 5.73375 24.265C5.24375 23.775 4.99917 23.1867 5 22.5V20C5 19.6458 5.12 19.3488 5.36 19.1088C5.6 18.8688 5.89667 18.7492 6.25 18.75C6.60417 18.75 6.90125 18.87 7.14125 19.11C7.38125 19.35 7.50084 19.6467 7.5 20V22.5H22.5V20C22.5 19.6458 22.62 19.3488 22.86 19.1088C23.1 18.8688 23.3967 18.7492 23.75 18.75C24.1042 18.75 24.4013 18.87 24.6413 19.11C24.8813 19.35 25.0008 19.6467 25 20V22.5C25 23.1875 24.755 23.7763 24.265 24.2663C23.775 24.7563 23.1867 25.0008 22.5 25H7.5Z" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        </article>
                    </div>

                    <aside class="b-details__grid-aside" aria-label="Horarios">
                        <div class="b-details__card b-details__card--featured b-details__card--featured-schedule b-details__card--sticky">
                            <h2 class="b-details__card-title b-details__card-title--with-icon">
                                <svg class="b-details__card-title-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="22" height="22">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                                <span>Horarios</span>
                            </h2>
                            <p class="b-details__card-sub">Consulta cuándo abrimos.</p>
                            <div class="b-details__schedule-list">
                                <EDCScheduleItem day="Lunes" :open_24="info.monday_open_24" :close_24="info.monday_closed24" :open_1="info.monday_open" :close_1="info.monday_closed" :open_2="info.monday_open_2" :close_2="info.monday_closed_2" />
                                <EDCScheduleItem day="Martes" :open_24="info.tuesday_open_24" :close_24="info.tuesday_closed24" :open_1="info.tuesday_open" :close_1="info.tuesday_closed" :open_2="info.tuesday_open_2" :close_2="info.tuesday_closed_2" />
                                <EDCScheduleItem day="Miércoles" :open_24="info.wednesday_open_24" :close_24="info.wednesday_closed24" :open_1="info.wednesday_open" :close_1="info.wednesday_closed" :open_2="info.wednesday_open_2" :close_2="info.wednesday_closed_2" />
                                <EDCScheduleItem day="Jueves" :open_24="info.thursday_open_24" :close_24="info.thursday_closed24" :open_1="info.thursday_open" :close_1="info.thursday_closed" :open_2="info.thursday_open_2" :close_2="info.thursday_closed_2" />
                                <EDCScheduleItem day="Viernes" :open_24="info.friday_open_24" :close_24="info.friday_closed24" :open_1="info.friday_open" :close_1="info.friday_closed" :open_2="info.friday_open_2" :close_2="info.friday_closed_2" />
                                <EDCScheduleItem day="Sábado" :open_24="info.saturday_open_24" :close_24="info.saturday_closed24" :open_1="info.saturday_open" :close_1="info.saturday_closed" :open_2="info.saturday_open_2" :close_2="info.saturday_closed_2" />
                                <EDCScheduleItem day="Domingo" :open_24="info.sunday_open_24" :close_24="info.sunday_closed24" :open_1="info.sunday_open" :close_1="info.sunday_closed" :open_2="info.sunday_open_2" :close_2="info.sunday_closed_2" />
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>

    <div class="b-details__panel" v-show="activeTab === 'services'">
        <div class="b-details__panel-inner b-details__panel-inner--wide">
            <div class="b-details__services">
                <EDCServiceTypeFilter
                    v-if="serviceTypesResolved && availableServiceTypes.length > 0"
                    :available-types="availableServiceTypes"
                    :selected-type="services_filter"
                    @filter_change="getBusinessServices"
                />
                <div
                    class="b-details__empty-state b-details__empty-state--no-services"
                    v-if="serviceTypesResolved && availableServiceTypes.length === 0"
                >
                    <p class="b-details__empty-title">Sin servicios publicados</p>
                    <p class="b-details__empty-text">Este negocio aún no tiene servicios en el catálogo.</p>
                </div>
                <div class="b-details__empty-state" v-show="serviceTypesResolved && availableServiceTypes.length > 0 && !services.length">
                    <p class="b-details__empty-title">Sin servicios en esta categoría</p>
                    <p class="b-details__empty-text">Prueba otro tipo de servicio o vuelve más tarde.</p>
                </div>
                <div class="b-details__services-list" v-show="serviceTypesResolved && services.length">
                    <div class="b-details__service" v-for="service in services" :key="service.id" @click="getServiceDetail(service.id)">
                        <div class="b-details__service-image">
                            <span v-if="service.service_type" class="b-details__service-badge">{{ serviceTypeLabel(service.service_type) }}</span>
                            <img :src="service.image" :alt="service.title || service.name || ''" />
                        </div>
                        <div class="b-details__service-info">
                            <p class="b-details__service-title">{{ service.title || service.name }}</p>
                            <p class="b-details__service-description" v-if="service.description">{{ truncateDescription(service.description) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="b-details__panel" v-show="activeTab === 'ratings'">
        <div class="b-details__panel-inner b-details__panel-inner--wide">
            <div class="b-details__ratings">
                <div class="b-details__rating-head">
                    <div class="b-details__cta-card rating-authenticated" v-show="logged_in && !rating.own">
                        <p class="rating-authenticated__description">¿Visitaste este lugar? Tu opinión ayuda a otros viajeros.</p>
                        <EDCButton label="Escribir reseña" @click="showRateModal(true)" />
                    </div>
                    <div class="b-details__cta-card rating-authenticated" v-show="!logged_in">
                        <p class="rating-authenticated__description">Crea una cuenta gratuita para dejar tu calificación.</p>
                        <EDCButton label="Registrarme" @click="openRegisterModal()" />
                    </div>
                </div>
                <div class="rating-avg" v-show="rating.list?.length">
                    <h2 class="rating-avg__value">{{ rating.average }}</h2>
                    <div class="rating-avg__details">
                        <EDCStars :quantity="rating.average" :star_size="17" golden />
                        <p class="rating-avg__total">
                            {{ rating.count }} {{ Number(rating.count) === 1 ? 'reseña' : 'reseñas' }}
                        </p>
                    </div>
                </div>
                <section class="b-details__reviews-showcase" v-show="rating.list?.length" aria-labelledby="reviews-showcase-title">
                    <header class="b-details__reviews-showcase-head">
                        <div class="b-details__reviews-showcase-brand" aria-hidden="true">
                            <svg
                                class="b-details__reviews-showcase-ico"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                                    stroke="#398bb6"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <div class="b-details__reviews-showcase-titles">
                            <p class="b-details__reviews-showcase-kicker">Voces del territorio</p>
                            <h2 id="reviews-showcase-title" class="b-details__reviews-showcase-title">Comentarios</h2>
                            <p class="b-details__reviews-showcase-lead">
                                Relatos y valoraciones de quienes ya estuvieron aquí. Te ayudan a imaginar la visita y a decidir con más tranquilidad.
                            </p>
                        </div>
                    </header>
                    <div class="b-details__review-list">
                        <article v-for="review in rating.list" :key="review.id" class="b-details__review-card">
                            <div class="b-details__review-card__shine" aria-hidden="true" />
                            <div class="b-details__review-card__body">
                                <div class="b-details__review-card__row">
                                    <div class="b-details__review-mono" aria-hidden="true">{{ reviewMonogram(review.user) }}</div>
                                    <div class="b-details__review-card__who">
                                        <span class="b-details__review-author">{{ review.user }}</span>
                                        <div v-if="reviewDateLabel(review)" class="b-details__review-card__when">
                                            <time :datetime="reviewDateIso(review) || undefined">{{ reviewDateLabel(review) }}</time>
                                        </div>
                                    </div>
                                    <div class="b-details__review-pill">
                                        <EDCStars class="b-details__review-stars" :quantity="review.rating" :star_size="17" golden />
                                    </div>
                                </div>
                                <blockquote class="b-details__review-quote">
                                    <span class="b-details__review-quote__mark" aria-hidden="true">“</span>
                                    <p class="b-details__review-text" v-html="reviewCommentHtml(review.comment)"></p>
                                </blockquote>
                            </div>
                        </article>
                    </div>
                </section>
                <div class="b-details__empty-state b-details__empty-state--ratings" v-show="rating.list && !rating.list.length">
                    <p class="b-details__empty-title">Sé el primero en calificar</p>
                    <p class="b-details__empty-text">Aún no hay reseñas. Comparte tu experiencia y ayuda a la comunidad.</p>
                </div>
            </div>
        </div>
    </div>
    </div>

    <EDCServiceDetail 
    v-show="service_detail.modal" 
    :data="service_detail.data" 
    :type="service_detail.type"
    @close="openServiceModal(false)" />
    <div class="rate-form" v-show="rate_form">
        <div class="rate-form__overlay" @click="showRateModal(false)"></div>
        <form class="rate-form__content" @submit="onRateModalSubmit">
            <div class="rate-form__ratings-stars">
                <EDCStars :editable="true" :quantity="rate.stars" :star_size="32" golden @star_clicked="onFormStarClicked" />
            </div>
            <input 
            class="rate-form__input-stars" 
            type="number" 
            min="1" 
            max="5" 
            v-model="rate.stars" 
            required>
            <textarea 
            class="rate-form__input-comment" 
            v-model="rate.comment" 
            placeholder="Escribe aquí tu opinion sobre este sitio"
            required>
            </textarea>
            <div class="rate-form__options">
                <EDCButton label="Enviar" />
                <a class="rate-form__close" @click="showRateModal(false)">Cerrar</a>
            </div>
        </form>
    </div>
    <EDCSpinner type="fixed" :show="spinner" />
    <EDCFooter />
    <EDCMobileNav />
</template>

<script>
import Splide from '@splidejs/splide';

import EDCHeader from '../components/Header.vue';
import EDCHead from '../components/Head.vue';
import EDCMobileNav from '../components/MobileNav.vue';
import EDCFooter from '../components/Footer.vue';
import EDCButton from '../components/Button.vue'
import EDCStars from '../components/Stars.vue';
import EDCScheduleItem from '../components/ScheduleItem.vue';
import EDCServiceTypeFilter from '../components/ServiceTypeFilter.vue';
import EDCServiceDetail from '../components/ServiceDetail.vue';
import EDCSpinner from '../components/Spinner.vue';

import { Auth } from '@/services/auth';
import { BusinessesAPI } from '@/services/businesses';
import { AuthModal } from '@/scripts/open-auth';
import { RatingAPI } from '@/services/rating';
import { Alert } from '@/scripts/open-alert.js';
import { whatsappUrlForPhone, telUrlForPhone } from '@/utils/phoneLinks';
import { linkifyPlainText } from '@/utils/linkifyPlainText';
import { API } from '@/services/_API';

export default {
    name: "sen-business-detail",
    components: {
        EDCHeader,
        EDCHead,
        EDCMobileNav,
        EDCFooter,
        EDCButton,
        EDCStars,
        EDCScheduleItem,
        EDCServiceTypeFilter,
        EDCServiceDetail,
        EDCSpinner

    },
    data() {
        return {
            activeTab: 'info',
            rate: {
                stars: null,
                comment: null
            },
            rate_form: false,
            logged_in: false,
            rating: {
                list: null,
                average: null,
                count: null,
            },
            images_splide: null,
            info: {},
            services_filter: 'PLA',
            availableServiceTypes: [],
            serviceTypesResolved: false,
            services: [],
            service_detail: {
                modal: false,
                data: null,
                type: null
            },
            spinner: false

        }
    },
    computed: {
        businessPhones() {
            const arr = [];
            if (this.info.telephone_main) arr.push(this.info.telephone_main);
            if (this.info.telephone_secondary) arr.push(this.info.telephone_secondary);
            return arr;
        },
        hasBusinessAmenities() {
            const i = this.info;
            if (!i || typeof i !== 'object') return false;
            return !!(i.wifi || i.ac || i.mobile_network || i.parking_service || i.food_service);
        },
        googleMapsUrl() {
            const parts = [this.info.address, this.info.municipio, 'Colombia'].filter(Boolean);
            if (!parts.length) return '';
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
        },
        whatsappPrefillMessage() {
            const name = (this.info?.name && String(this.info.name).trim()) || 'su negocio';
            return `Vengo de la página de Enamórate del Cesar. Quisiera conocer más información sobre ${name}.`;
        },
        businessPhoneRows() {
            return this.businessPhones.map((tel) => {
                const wa = whatsappUrlForPhone(tel, { text: this.whatsappPrefillMessage });
                const attrs = wa
                    ? { href: wa, target: '_blank', rel: 'noopener noreferrer' }
                    : { href: telUrlForPhone(tel) || '#' };
                return {
                    tel,
                    whatsapp: !!wa,
                    attrs,
                    ariaLabel: wa
                        ? `Abrir WhatsApp al número ${tel}`
                        : `Llamar al número ${tel}`,
                };
            });
        },
        primaryWhatsappUrl() {
            const row = this.businessPhoneRows?.find((r) => r.whatsapp);
            if (row?.attrs?.href) return row.attrs.href;
            const w = this.info?.whatsapp;
            if (!w) return '';
            const d = String(w).replace(/\D/g, '');
            if (d.length < 10) return '';
            const base = `https://wa.me/57${d.slice(-10)}`;
            return `${base}?text=${encodeURIComponent(this.whatsappPrefillMessage)}`;
        },
        infoWhatsappSocialUrl() {
            const w = this.info?.whatsapp;
            if (!w) return '';
            const d = String(w).replace(/\D/g, '');
            if (d.length < 10) return '';
            const base = `https://wa.me/57${d.slice(-10)}`;
            return `${base}?text=${encodeURIComponent(this.whatsappPrefillMessage)}`;
        },
        businessDescriptionHtml() {
            if (!this.info?.description) return '';
            return linkifyPlainText(String(this.info.description));
        },
    },
    methods: {
        setTab(tabName) {
            this.activeTab = tabName;
        },
        reviewCommentHtml(comment) {
            return linkifyPlainText(comment || '');
        },
        attachmentFileUrl(item) {
            const raw =
                item?.file ??
                item?.file_url ??
                item?.url ??
                item?.attachment ??
                item?.document;
            if (raw == null || raw === '') return '#';
            let u = String(raw).trim();
            if (/^https?:\/\//i.test(u)) {
                if (/\s/.test(u)) {
                    try {
                        u = encodeURI(decodeURI(u));
                    } catch {
                        u = encodeURI(u);
                    }
                }
                return u;
            }
            if (u.startsWith('//')) return `https:${u}`;
            const base = String(API).replace(/\/?api\/?$/i, '').replace(/\/$/, '');
            if (u.startsWith('/')) return `${base}${u}`;
            return `${base}/${u}`;
        },
        onCalificarClick() {
            this.setTab('ratings');
            this.$nextTick(() => {
                if (this.logged_in && !this.rating.own) {
                    this.showRateModal(true);
                }
            });
        },
        destroyImagesSplide() {
            if (this.images_splide) {
                this.images_splide.destroy();
                this.images_splide = null;
            }
        },
        setupImagesSplide() {
            this.destroyImagesSplide();
            if (!this.info.images?.length) return;
            if (!document.querySelector('#images-splide')) return;
            this.images_splide = new Splide('#images-splide', {
                type: 'slide',
                fixedWidth: 180,
                gap: 12,
                pagination: false,
                arrows: true,
                drag: false,
            }).mount();
        },
        async openGalleryAt(index) {
            const imgs = this.info.images;
            if (!imgs?.length || index < 0 || index >= imgs.length) return;
            if (typeof window !== 'undefined' && window.pswp) {
                try {
                    window.pswp.destroy();
                } catch (_) {
                    delete window.pswp;
                }
            }
            const dataSource = imgs.map((img) => ({
                src: img.file,
                width: Number(img.width) || 1600,
                height: Number(img.height) || 1067,
            }));
            const PhotoSwipe = (await import('photoswipe')).default;
            const pswp = new PhotoSwipe({
                dataSource,
                index,
                bgOpacity: 0.92,
            });
            window.pswp = pswp;
            pswp.on('destroy', () => {
                delete window.pswp;
            });
            pswp.init();
        },
        showRateModal(show) {
            if (!show) {
                this.rate_form = false
                this.rate = { comment: null, stars: null }
            } else {
                this.rate_form = true
            }
        },
        onRateModalSubmit (event) {
            event.preventDefault()
            const businessID = this.$route.params.id;
            this.showSpinner(true)
            RatingAPI.sendRating(this.rate.stars, businessID ,this.rate.comment).then(data => {
                this.showSpinner(false)
                if (data.status === 400) {
                    this.login.error = data.msg
                } else {
                    Alert.open("success", "Reseña enviada correctamente")
                    this.showRateModal(false)
                    this.getBusinessReviews()
                }
            })
        },
        onAuthenticationChange(payload) {
            const status = payload.logged_in;
            this.logged_in = status;
        },
        onFormStarClicked(number) {
            this.rate.stars = number;
        },
        getBusinessInfo() {
            const businessID = this.$route.params.id;
            this.showSpinner(true);
            return BusinessesAPI.getBusiness(businessID).then((data) => {
                this.showSpinner(false);
                if (data) {
                    this.info = data;
                }
            });
        },
        getBusinessType(code) {
            const codes = {
                RES: 'Restaurante',
                TRA: 'Transporte',
                HOS: 'Hospedaje',
                TOU: 'Tour',
            };
            return codes[code] || 'Negocio';
        },
        getBusinessServices(type) {
            const businessID = this.$route.params.id;
            if (this.availableServiceTypes.length && !this.availableServiceTypes.includes(type)) {
                return;
            }
            this.services_filter = type;

            BusinessesAPI.getServices(businessID, type).then((data) => {
                this.services = data.results || [];
            });
        },
        async resolveAvailableServiceTypes() {
            const businessID = this.$route.params.id;
            const ORDER = ['PLA', 'HOS', 'TRA', 'TOU'];
            this.serviceTypesResolved = false;
            try {
                const flags = await Promise.all(
                    ORDER.map((t) => BusinessesAPI.hasServices(businessID, t)),
                );
                this.availableServiceTypes = ORDER.filter((_, i) => flags[i]);
            } catch (e) {
                console.error('resolveAvailableServiceTypes', e);
                this.availableServiceTypes = [...ORDER];
            }
            this.serviceTypesResolved = true;
            if (this.availableServiceTypes.length === 0) {
                this.services = [];
                return;
            }
            if (!this.availableServiceTypes.includes(this.services_filter)) {
                this.services_filter = this.availableServiceTypes[0];
            }
            this.getBusinessServices(this.services_filter);
        },
        getBusinessReviews() {
            const businessID = this.$route.params.id;
    
            BusinessesAPI.getReviews(businessID).then(data => {
                this.rating.list = data.results;

                this.rating.average = `${data.average}`;
                this.rating.count = data.count;
                this.rating.own = Object.keys(data.user_comment).length != 0 ? true : false
                if (data.user_comment?.user) {
                    this.rating.list.unshift(data.user_comment)
                    this.rating.count += 1;
                }
            })
        },
        openServiceModal(open) {
            this.service_detail.modal = open
        },
        getServiceDetail(id) {
            const typeMap = {
                'PLA': 1,
                'TRA': 2,
                'HOS': 3,
                'TOU': 4
            };
            this.service_detail.type = typeMap[this.services_filter] || 1;

            BusinessesAPI.getServiceDetails(id).then((data) => {
                if (!data || data instanceof Error) return;
                this.service_detail.data = data;
                if (data.service_type && typeMap[data.service_type] != null) {
                    this.service_detail.type = typeMap[data.service_type];
                }
                this.openServiceModal(true);
            }).catch((error) => {
                console.error('Error loading service details:', error);
            });
        },
        serviceTypeLabel(code) {
            const labels = {
                PLA: 'Gastronomía',
                TRA: 'Transporte',
                HOS: 'Hospedaje',
                TOU: 'Tour',
            };
            return labels[code] || code || '';
        },
        reviewMonogram(name) {
            if (!name || typeof name !== 'string') return '?';
            const c = name.trim().charAt(0);
            return c ? c.toUpperCase() : '?';
        },
        reviewDateLabel(review) {
            if (!review || typeof review !== 'object') return '';
            const raw = review.created_at || review.date || review.created || review.timestamp;
            if (!raw) return '';
            try {
                const date = new Date(raw);
                if (Number.isNaN(date.getTime())) return '';
                return new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium' }).format(date);
            } catch {
                return '';
            }
        },
        reviewDateIso(review) {
            if (!review || typeof review !== 'object') return '';
            const raw = review.created_at || review.date || review.created || review.timestamp;
            if (!raw) return '';
            try {
                const date = new Date(raw);
                if (Number.isNaN(date.getTime())) return '';
                return date.toISOString();
            } catch {
                return '';
            }
        },
        truncateDescription(description) {
            if (!description) return '';
            let t = String(description).replace(/°+/g, ' ').replace(/\s+/g, ' ').trim();
            if (t.length <= 100) return t;
            return `${t.slice(0, 100)}…`;
        },
        openRegisterModal() {
            AuthModal.openModal("register")
        },
        showSpinner(show) {
            this.spinner = show
        }
    },
    watch: {
        '$route.params.id'(newId, oldId) {
            if (newId === oldId) return;
            this.services = [];
            this.availableServiceTypes = [];
            this.serviceTypesResolved = false;
            this.getBusinessInfo().then(async () => {
                await this.$nextTick();
                this.destroyImagesSplide();
                this.setupImagesSplide();
                this.getBusinessReviews();
                await this.resolveAvailableServiceTypes();
            });
        },
    },
    async mounted() {
        this.logged_in = Auth.isAuthenticated();
        Auth.subscribe(this.onAuthenticationChange);

        await this.getBusinessInfo();
        await this.$nextTick();
        this.setupImagesSplide();

        this.getBusinessReviews();
        await this.resolveAvailableServiceTypes();
    },
    beforeUnmount() {
        Auth.unsubscribe(this.onAuthenticationChange);
        this.destroyImagesSplide();
        if (typeof window !== 'undefined' && window.pswp) {
            try {
                window.pswp.destroy();
            } catch (_) {
                delete window.pswp;
            }
        }
    },
}
</script>

<style>
/* Página ficha negocio — layout actual (hero, intro, tabs, grid 2 cols, servicios, reseñas) */

.b-details-page {
    width: 100%;
    min-height: 60vh;
    background: var(--color-light, #f8f6fa);
    padding-bottom: 48px;
}

/* Hero */
.b-details__hero {
    width: 100%;
    height: 260px;
    overflow: hidden;
    position: relative;
}

.b-details__hero-bk {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 0;
}

.b-details__hero-bk::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.72) 100%);
    z-index: 1;
}

.b-details__hero-bk img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Intro (tarjeta título + acciones) */
.b-details__intro {
    background: var(--color-light, #f8f6fa);
    padding: 16px 16px 8px;
    margin-top: -56px;
    position: relative;
    z-index: 2;
}

.b-details__intro--no-hero {
    margin-top: 0;
    padding-top: 24px;
}

.b-details__intro-inner {
    position: relative;
    overflow: hidden;
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px 20px 22px;
    background: #fff;
    border-radius: 16px;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.07),
        0 4px 24px rgba(177, 205, 74, 0.14);
    border: 1px solid rgba(177, 205, 74, 0.28);
}

.b-details__intro-inner::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #b1cd4a;
    pointer-events: none;
}

.b-details__intro-inner > * {
    position: relative;
    z-index: 1;
}

.b-details__badge {
    display: inline-block;
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-primary, #a153aa);
    margin-bottom: 8px;
}

.b-details__title {
    font-size: clamp(1.5rem, 4vw, 2rem);
    font-weight: 700;
    margin: 0 0 10px;
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: #b1cd4a;
}

.b-details__title::after {
    content: '';
    display: block;
    width: min(240px, 100%);
    height: 3px;
    margin-top: 10px;
    border-radius: 3px;
    background: #b1cd4a;
}

.b-details__rating-actions-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 14px;
}

.b-details__rating-row {
    margin-bottom: 0;
    flex: 1 1 auto;
    min-width: min(100%, 220px);
}

.b-details__rating-inline {
    display: flex;
    align-items: center;
    gap: 10px;
}

.b-details__rating-num {
    font-weight: 600;
    color: var(--color-dark, #222);
    font-size: 1rem;
}

.b-details__rating-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 0.9375rem;
    color: #666;
}

.b-details__rating-empty-ico {
    flex-shrink: 0;
    color: #d4a017;
    filter: drop-shadow(0 1px 1px rgba(180, 120, 20, 0.25));
}

.b-details__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    flex: 0 1 auto;
    margin-top: -2px;
}

.b-details__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 16px;
    border-radius: 10px;
    font-size: 0.9375rem;
    font-weight: 600;
    text-decoration: none;
    border: 2px solid transparent;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    font-family: inherit;
    line-height: 1.2;
}

.b-details__btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.b-details__btn-ico {
    flex-shrink: 0;
}

.b-details__btn--whatsapp {
    background: #25d366;
    color: #fff;
    border-color: #25d366;
}

.b-details__btn--whatsapp:hover {
    background: #1fb855;
    border-color: #1fb855;
    color: #fff;
}

.b-details__btn--primary {
    background: var(--color-primary, #a153aa);
    color: #fff;
    border-color: var(--color-primary, #a153aa);
}

.b-details__btn--primary:hover {
    filter: brightness(1.05);
    color: #fff;
}

.b-details__btn--outline {
    background: #fff;
    color: var(--color-dark, #222);
    border-color: rgba(0, 0, 0, 0.18);
}

.b-details__btn--outline:hover {
    border-color: var(--color-primary, #a153aa);
    color: var(--color-primary, #a153aa);
}

/* Pestañas */
.b-details__tabs {
    position: sticky;
    top: 0;
    z-index: 5;
    width: 100%;
    padding: 0 12px;
    background: var(--color-light, #f8f6fa);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.b-details__tabs-inner {
    max-width: 1000px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    gap: 4px;
    padding: 6px 0 0;
}

.b-details__tab {
    flex: 1;
    max-width: 220px;
    padding: 14px 12px;
    border: none;
    background: transparent;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    border-radius: 10px 10px 0 0;
    transition: color 0.2s, background 0.2s;
}

/* Pestañas con acento por sección: morado / verde / azul */
.b-details__tab--info:hover {
    color: var(--color-primary, #a153aa);
    background: rgba(161, 83, 170, 0.09);
}

.b-details__tab--info.is-active {
    color: var(--color-primary, #a153aa);
    background: #fff;
    box-shadow: 0 -3px 0 var(--color-primary, #a153aa) inset;
}

.b-details__tab--services:hover {
    color: #4a5c16;
    background: rgba(177, 205, 74, 0.22);
}

.b-details__tab--services.is-active {
    color: #4a5c16;
    background: #fff;
    box-shadow: 0 -3px 0 #b1cd4a inset;
}

.b-details__tab--ratings:hover {
    color: #2f7aa3;
    background: rgba(57, 139, 182, 0.1);
}

.b-details__tab--ratings.is-active {
    color: #2f7aa3;
    background: #fff;
    box-shadow: 0 -3px 0 #398bb6 inset;
}

/* Paneles de contenido */
.b-details__panel {
    width: 100%;
    padding: 20px 16px 0;
}

.b-details__panel-inner {
    max-width: 1000px;
    margin: 0 auto;
}

.b-details__panel-inner--wide {
    max-width: 1100px;
}

/* Galería + Splide */
.b-details__gallery-wrap {
    margin-bottom: 22px;
}

.b-images-splide__container {
    width: 100%;
    margin: 0 0 8px;
}

/* Flechas galería: más ligeras y legibles que el estilo global de Splide */
.b-details-splide {
    position: relative;
}

/* El global usa height:0 en .splide__arrows y las flechas quedan arriba; centramos en el carril */
.b-details-splide .splide__arrows {
    top: 50%;
    bottom: auto;
    height: auto;
    margin-top: 0;
    padding: 0 6px;
    transform: translateY(-50%);
    align-items: center;
}

.b-details-splide .splide__arrow {
    width: 40px;
    height: 40px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    box-shadow:
        0 2px 10px rgba(0, 0, 0, 0.1),
        0 1px 3px rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    opacity: 1;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        background 0.2s ease,
        border-color 0.2s ease;
}

.b-details-splide .splide__arrow svg {
    width: 18px;
    height: 18px;
    fill: rgba(34, 34, 34, 0.88);
    transition: fill 0.2s ease;
}

.b-details-splide .splide__arrow:hover:not(:disabled) {
    background: #fff;
    border-color: rgba(57, 139, 182, 0.45);
    box-shadow:
        0 4px 18px rgba(0, 0, 0, 0.12),
        0 2px 6px rgba(57, 139, 182, 0.18);
}

.b-details-splide .splide__arrow--next:hover:not(:disabled) {
    transform: scale(1.06);
}

.b-details-splide .splide__arrow--prev:hover:not(:disabled) {
    transform: rotate(180deg) scale(1.06);
}

.b-details-splide .splide__arrow:active:not(:disabled) {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
}

.b-details-splide .splide__arrow--next:active:not(:disabled) {
    transform: scale(1.02);
}

.b-details-splide .splide__arrow--prev:active:not(:disabled) {
    transform: rotate(180deg) scale(1.02);
}

.b-details-splide .splide__arrow:hover:not(:disabled) svg {
    fill: #398bb6;
}

.b-details-splide .splide__arrow:focus-visible {
    outline: 2px solid rgba(57, 139, 182, 0.65);
    outline-offset: 2px;
}

.b-details-splide .splide__arrow:disabled {
    opacity: 0.32;
    cursor: not-allowed;
}

.b-image-slide {
    height: 132px;
    border-radius: 12px;
    overflow: hidden;
}

.b-image-slide__link {
    display: block;
    width: 100%;
    height: 100%;
    cursor: zoom-in;
}

.b-image-slide img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.b-image-slide__link:hover img {
    transform: scale(1.03);
}

/* Grid principal + aside horarios */
.b-details__grid {
    display: grid;
    gap: 28px;
    align-items: start;
}

.b-details__grid-main {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.b-details__card {
    background: #fff;
    border-radius: 14px;
    padding: 20px 20px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

/* Tarjetas principales: Ubicación, Sobre el lugar, Contacto */
.b-details__card--featured {
    position: relative;
    overflow: hidden;
    border-color: rgba(161, 83, 170, 0.22);
    box-shadow:
        0 4px 22px rgba(161, 83, 170, 0.1),
        0 2px 12px rgba(0, 0, 0, 0.05);
}

.b-details__card--featured::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
        90deg,
        var(--color-primary, #a153aa) 0%,
        rgba(200, 130, 205, 0.85) 45%,
        rgba(161, 83, 170, 0.35) 100%
    );
    pointer-events: none;
}

.b-details__card--featured::after {
    content: '';
    position: absolute;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    pointer-events: none;
    opacity: 0.55;
    background: radial-gradient(
        circle at center,
        rgba(161, 83, 170, 0.14) 0%,
        rgba(161, 83, 170, 0.04) 45%,
        transparent 70%
    );
}

.b-details__card--featured-loc::after {
    bottom: -36px;
    right: -28px;
}

.b-details__card--featured-about::after {
    bottom: -40px;
    left: -32px;
    background: radial-gradient(
        circle at center,
        rgba(180, 100, 185, 0.12) 0%,
        rgba(161, 83, 170, 0.03) 50%,
        transparent 72%
    );
}

.b-details__card--featured-contact::after {
    top: -48px;
    right: -36px;
    width: 100px;
    height: 100px;
    opacity: 0.5;
}

.b-details__card--featured-schedule::after {
    bottom: -34px;
    left: -30px;
    opacity: 0.48;
}

.b-details__card--featured-documents::after {
    bottom: -32px;
    right: -26px;
    opacity: 0.5;
}

.b-details__card--featured > * {
    position: relative;
    z-index: 1;
}

.b-details__card-title {
    margin: 0 0 6px;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-primary, #a153aa);
}

.b-details__card-title--with-icon {
    display: flex;
    align-items: center;
    gap: 10px;
}

.b-details__card-title-ico {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    color: var(--color-primary, #a153aa);
    opacity: 0.92;
}

.b-details__card-sub {
    margin: 0 0 14px;
    font-size: 0.875rem;
    color: #666;
    line-height: 1.45;
}

.b-details__text {
    margin: 0 0 8px;
    font-size: 1rem;
    color: var(--color-dark, #222);
    line-height: 1.5;
}

.b-details__text-muted {
    margin: 0 0 14px;
    font-size: 0.9375rem;
    color: #777;
}

.b-details__municipio {
    margin: 0 0 14px;
    font-size: 1.0625rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--color-primary, #a153aa);
}

.b-details__description {
    margin: 0 0 14px;
    font-size: 1rem;
    color: var(--color-dark, #222);
    line-height: 1.65;
    text-align: justify;
}

.b-details__description .edc-linkified {
    color: var(--color-primary, #a153aa);
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-word;
}

.b-details__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.b-details__chip {
    display: inline-block;
    padding: 6px 12px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e4a63;
    background: rgba(57, 139, 182, 0.12);
    border-radius: 999px;
    box-shadow: 0 1px 4px rgba(57, 139, 182, 0.22), 0 1px 2px rgba(57, 139, 182, 0.12);
}

.b-details__contact-cols {
    display: grid;
    gap: 14px;
    margin-bottom: 12px;
    grid-template-columns: 1fr;
}

.b-details__contact-cols--two {
    grid-template-columns: auto auto;
    justify-content: start;
    align-items: start;
    column-gap: 18px;
    row-gap: 10px;
}

.b-details__contact-cols .b-details__contact-actions {
    margin-bottom: 0;
    min-width: 0;
}

.b-details__contact-cols--two .b-details__contact-actions:first-of-type {
    flex-direction: column;
    align-items: flex-start;
}

.b-details__contact-cols--two .b-details__contact-actions--phones {
    flex-direction: column;
    align-items: flex-start;
}

.b-details__contact-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
}

.b-details__contact-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    background: rgba(57, 139, 182, 0.12);
    color: #1e4a63;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9375rem;
    border: 1px solid rgba(57, 139, 182, 0.28);
    box-shadow: 0 1px 4px rgba(57, 139, 182, 0.22), 0 1px 2px rgba(57, 139, 182, 0.12);
    transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.b-details__contact-btn:hover {
    background: rgba(57, 139, 182, 0.18);
    border-color: rgba(57, 139, 182, 0.42);
    box-shadow: 0 2px 8px rgba(57, 139, 182, 0.28);
}

.b-details__contact-btn--mail {
    word-break: break-all;
}

.b-details__contact-ico {
    flex-shrink: 0;
}

.b-details__social-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 6px;
}

.b-details__social-item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(57, 139, 182, 0.1);
    border: 1px solid rgba(57, 139, 182, 0.22);
    box-shadow: 0 1px 4px rgba(57, 139, 182, 0.2), 0 1px 2px rgba(57, 139, 182, 0.1);
    transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
}

.b-details__social-item:hover {
    transform: translateY(-2px);
    background: rgba(57, 139, 182, 0.14);
    box-shadow: 0 4px 14px rgba(57, 139, 182, 0.28);
}

.b-details__social-item img {
    width: 24px;
    height: 24px;
    object-fit: contain;
}

.b-details__file-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 6px;
}

.b-details__file-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    background: #fafafa;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
}

.b-details__file-item-content {
    flex: 1;
    min-width: 0;
}

.b-details__file-name {
    margin: 0;
    font-weight: 600;
    font-size: 1rem;
    color: var(--color-dark, #222);
}

.b-details__download-file {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 8px;
    color: var(--color-primary, #a153aa);
    transition: background 0.2s;
}

.b-details__download-file:hover {
    background: rgba(161, 83, 170, 0.1);
}

.b-details__download-file svg {
    width: 28px;
    height: 28px;
    fill: currentColor;
}

.b-details__schedule-list {
    margin-top: 4px;
}

.b-details__card--sticky {
    position: sticky;
    top: 64px;
}

/* Pestaña servicios */
.b-details__services {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.b-details__empty-state {
    text-align: center;
    padding: 36px 22px;
    background: #fff;
    border-radius: 14px;
    border: 1px dashed rgba(161, 83, 170, 0.35);
}

.b-details__empty-title {
    margin: 0 0 8px;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--color-dark, #222);
}

.b-details__empty-text {
    margin: 0;
    font-size: 0.9375rem;
    color: #666;
    line-height: 1.5;
}

.b-details__empty-state--no-services {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(177, 205, 74, 0.42);
    background: rgba(177, 205, 74, 0.07);
    box-shadow:
        0 4px 22px rgba(177, 205, 74, 0.14),
        0 1px 3px rgba(0, 0, 0, 0.04);
}

.b-details__empty-state--no-services::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
        90deg,
        #b1cd4a 0%,
        rgba(177, 205, 74, 0.75) 55%,
        rgba(177, 205, 74, 0.35) 100%
    );
    pointer-events: none;
}

.b-details__empty-state--no-services .b-details__empty-title {
    color: #4d6220;
}

.b-details__empty-state--no-services .b-details__empty-text {
    color: #5a6b38;
}

.b-details__empty-state--ratings {
    position: relative;
    overflow: hidden;
    margin-top: 8px;
    border: 1px solid rgba(57, 139, 182, 0.22);
    border-radius: 16px;
    box-shadow:
        0 4px 24px rgba(57, 139, 182, 0.08),
        0 1px 3px rgba(0, 0, 0, 0.04);
}

.b-details__empty-state--ratings::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: #398bb6;
    pointer-events: none;
}

.b-details__empty-state--ratings .b-details__empty-title {
    color: #2f7aa3;
}

.b-details__empty-state--ratings .b-details__empty-text {
    color: #3a4550;
}

.b-details__services-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    align-items: stretch;
}

.b-details__service {
    position: relative;
    width: 100%;
    min-height: 0;
    background-color: #fff;
    border-radius: 14px;
    border: 1px solid rgba(177, 205, 74, 0.35);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s ease;
    box-shadow:
        0 4px 18px rgba(177, 205, 74, 0.1),
        0 2px 10px rgba(0, 0, 0, 0.05);
    height: 100%;
}

.b-details__service::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
        90deg,
        #b1cd4a 0%,
        rgba(177, 205, 74, 0.82) 45%,
        rgba(130, 155, 50, 0.4) 100%
    );
    pointer-events: none;
    z-index: 2;
}

.b-details__service:hover {
    border-color: rgba(120, 145, 48, 0.55);
    box-shadow:
        0 8px 26px rgba(177, 205, 74, 0.2),
        0 2px 12px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
}

.b-details__service-image {
    position: relative;
    width: 100%;
    flex-shrink: 0;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    z-index: 0;
    background: linear-gradient(145deg, #f4f8ec 0%, #eef3e4 100%);
}

.b-details__service-image img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.b-details__service-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 1;
    padding: 4px 8px;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #fff;
    background: rgba(74, 92, 22, 0.94);
    border-radius: 6px;
    line-height: 1.2;
    max-width: calc(100% - 16px);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.b-details__service-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px 12px;
    background-color: #fff;
    min-height: 0;
}

.b-details__service-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--color-dark, #222);
    margin: 0;
    line-height: 1.25;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.b-details__service-description {
    font-size: 0.8125rem;
    color: #666;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: 0;
}

/* Reseñas */
.b-details__ratings {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-width: 720px;
    margin: 0 auto;
}

.b-details__rating-head {
    width: 100%;
}

.b-details__cta-card.rating-authenticated {
    margin-bottom: 0;
}

.rating-avg {
    position: relative;
    overflow: hidden;
    width: 100%;
    background-color: #fff;
    border: 1px solid rgba(57, 139, 182, 0.22);
    border-radius: 16px;
    padding: 22px 20px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 12px;
    margin-bottom: 22px;
    box-shadow: 0 4px 22px rgba(57, 139, 182, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rating-avg::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: #398bb6;
}

.rating-avg__value {
    font-size: 2.25rem;
    font-weight: 700;
    line-height: 1;
    margin: 0;
    color: var(--color-dark, #222);
}

.rating-avg__details {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.rating-avg__details :deep(.edc-stars) {
    display: flex;
    justify-content: center;
}

.rating-avg__details :deep(.edc-stars__list) {
    gap: 2px;
}

.rating-avg__total {
    margin: 0;
    font-size: 0.9375rem;
    color: #666;
}

.rating-own {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 24px;
    background-color: #f3f3f3;
    border: 2px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
}

.rating-own__item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rating-authenticated {
    position: relative;
    overflow: hidden;
    padding: 24px 22px 26px;
    background-color: #fff;
    border-radius: 16px;
    border: 1px solid rgba(57, 139, 182, 0.22);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    box-shadow: 0 4px 24px rgba(57, 139, 182, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rating-authenticated::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: #398bb6;
}

.rating-authenticated__description {
    text-align: center;
    margin: 0;
    font-size: 0.95rem;
    color: #3a4550;
    line-height: 1.55;
    max-width: 34em;
}

.rating-authenticated button {
    max-width: 220px;
}

.b-details__rating-list-title {
    width: 100%;
    margin: 0 0 14px;
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--color-primary, #a153aa);
}

/* Sección comentarios — showcase (fondo blanco, acentos #398bb6) */
.b-details__reviews-showcase {
    position: relative;
    margin-top: 8px;
    padding: 24px 20px 28px;
    border-radius: 20px;
    background: #fff;
    border: 1px solid rgba(57, 139, 182, 0.22);
    box-shadow: 0 4px 24px rgba(57, 139, 182, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}

.b-details__reviews-showcase::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: #398bb6;
}

.b-details__reviews-showcase-head {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 22px;
    position: relative;
    z-index: 1;
}

.b-details__reviews-showcase-brand {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid rgba(57, 139, 182, 0.28);
    box-shadow: 0 4px 14px rgba(57, 139, 182, 0.12);
}

.b-details__reviews-showcase-ico {
    width: 34px;
    height: 34px;
    display: block;
}

.b-details__reviews-showcase-titles {
    min-width: 0;
}

.b-details__reviews-showcase-kicker {
    margin: 0 0 4px;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #398bb6;
}

.b-details__reviews-showcase-title {
    margin: 0 0 6px;
    font-size: 1.35rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--color-dark, #1a1520);
    line-height: 1.2;
}

.b-details__reviews-showcase-lead {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.55;
    color: #5c5560;
    max-width: none;
    text-align: justify;
    text-justify: inter-word;
    hyphens: auto;
}

.b-details__review-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    z-index: 1;
}

.b-details__review-card {
    position: relative;
    border-radius: 18px;
    background: #fff;
    border: 1px solid rgba(57, 139, 182, 0.12);
    box-shadow:
        0 8px 28px rgba(57, 139, 182, 0.1),
        0 2px 10px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.b-details__review-card__shine {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: #398bb6;
    opacity: 1;
}

.b-details__review-card__body {
    position: relative;
    padding: 18px 18px 20px;
}

.b-details__review-card__row {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    flex-wrap: wrap;
}

.b-details__review-mono {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #fff;
    background: linear-gradient(145deg, #398bb6 0%, #2d6f94 100%);
    box-shadow: 0 6px 18px rgba(57, 139, 182, 0.35);
}

.b-details__review-card__who {
    flex: 1 1 140px;
    min-width: 0;
}

.b-details__review-author {
    display: block;
    font-size: 0.98rem;
    font-weight: 700;
    color: var(--color-dark, #1a1a1a);
    word-break: break-word;
    line-height: 1.3;
}

.b-details__review-card__when {
    margin-top: 4px;
    font-size: 0.78rem;
    font-weight: 500;
    color: #8b8792;
}

.b-details__review-card__when time {
    font-variant-numeric: tabular-nums;
}

.b-details__review-pill {
    flex-shrink: 0;
    margin-left: auto;
    padding: 6px 12px 6px 10px;
    border-radius: 999px;
    background: linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%);
    border: 1px solid rgba(234, 179, 8, 0.38);
    box-shadow: 0 1px 3px rgba(234, 179, 8, 0.15);
}

.b-details__review-stars :deep(.edc-stars__list) {
    gap: 2px;
}

.b-details__review-quote {
    position: relative;
    margin: 14px 0 0;
    padding: 0 0 0 14px;
    border: none;
    border-left: 3px solid rgba(57, 139, 182, 0.4);
}

@media (hover: hover) and (pointer: fine) {
    .b-details__review-card:hover {
        transform: translateY(-3px);
        box-shadow:
            0 14px 40px rgba(57, 139, 182, 0.16),
            0 4px 12px rgba(57, 139, 182, 0.1);
    }
}

.b-details__review-quote__mark {
    position: absolute;
    left: 2px;
    top: -6px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 2.75rem;
    line-height: 1;
    font-weight: 700;
    color: rgba(57, 139, 182, 0.16);
    pointer-events: none;
    user-select: none;
}

.b-details__review-text {
    position: relative;
    z-index: 1;
    margin: 0;
    padding-top: 4px;
    font-size: 1.02rem;
    line-height: 1.65;
    color: #3a3640;
    word-break: break-word;
    white-space: pre-wrap;
}

.b-details__review-text .edc-linkified {
    color: #2f7aa3;
    text-decoration: underline;
    text-underline-offset: 2px;
}

/* Modal calificar */
.rate-form {
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.rate-form__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.75);
    z-index: -1;
}

.rate-form__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    gap: 22px;
    padding: 28px;
    margin: 20px;
    position: relative;
    max-width: 420px;
    width: 100%;
}

.rate-form__ratings-stars {
    display: flex;
    justify-content: center;
    gap: 6px;
}

.rate-form__input-stars {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 70px;
}

.rate-form__ratings-stars img {
    width: 40px;
    height: 40px;
}

.rate-form__input-comment {
    width: 100%;
    min-height: 120px;
    resize: vertical;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    font-family: inherit;
}

.rate-form__options {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.rate-form__close {
    font-weight: 600;
    color: var(--color-primary, #a153aa);
    font-size: 1rem;
    text-decoration: underline;
    cursor: pointer;
}

/* —— Vista móvil —— */
@media (max-width: 639px) {
    .b-details-page {
        padding-bottom: max(32px, env(safe-area-inset-bottom, 0px));
        padding-left: env(safe-area-inset-left, 0);
        padding-right: env(safe-area-inset-right, 0);
    }

    .b-details__hero {
        height: 200px;
    }

    .b-details__intro {
        padding: 10px 10px 6px;
        margin-top: -40px;
    }

    .b-details__intro--no-hero {
        padding-top: 16px;
    }

    .b-details__intro-inner {
        padding: 14px 12px 16px;
        border-radius: 12px;
    }

    .b-details__title {
        font-size: 1.35rem;
        margin-bottom: 8px;
    }

    .b-details__rating-actions-row {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .b-details__rating-row {
        min-width: 0;
    }

    .b-details__actions {
        width: 100%;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        margin-top: 0;
        gap: 10px;
    }

    .b-details__btn {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        min-height: 46px;
        padding-left: 14px;
        padding-right: 14px;
    }

    .b-details__tabs {
        padding: 0 8px;
        top: env(safe-area-inset-top, 0);
    }

    .b-details__tabs-inner {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0;
        max-width: 100%;
        padding: 4px 0 0;
    }

    .b-details__tab {
        padding: 12px 6px;
        font-size: clamp(0.7rem, 2.9vw, 0.8125rem);
        line-height: 1.2;
        max-width: none;
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        hyphens: auto;
    }

    .b-details__panel {
        padding: 12px 10px 0;
    }

    .b-details__grid {
        gap: 18px;
    }

    .b-details__grid-main {
        gap: 16px;
    }

    .b-details__card {
        padding: 14px 12px;
        border-radius: 12px;
    }

    .b-details__gallery-wrap {
        margin-left: -2px;
        margin-right: -2px;
        margin-bottom: 16px;
    }

    .b-image-slide {
        height: 112px;
    }

    .b-details__ratings {
        max-width: none;
        padding: 0 2px;
    }

    .b-details__reviews-showcase {
        padding: 18px 14px 22px;
        border-radius: 16px;
    }

    .b-details__reviews-showcase-head {
        gap: 12px;
        margin-bottom: 18px;
    }

    .b-details__reviews-showcase-brand {
        width: 46px;
        height: 46px;
        border-radius: 14px;
    }

    .b-details__reviews-showcase-ico {
        width: 30px;
        height: 30px;
    }

    .b-details__reviews-showcase-title {
        font-size: 1.2rem;
    }

    .b-details__review-card__body {
        padding: 16px 14px 18px;
    }

    .b-details__review-pill {
        margin-left: 0;
    }

    .rating-avg {
        padding: 18px 16px 20px;
    }

    .rating-authenticated {
        padding: 16px;
    }

    .b-details__services-list {
        gap: 10px;
    }

    .b-details__empty-state {
        padding: 28px 14px;
    }

    .rate-form__content {
        margin: 12px;
        padding: 22px 18px;
        max-width: calc(100vw - 24px);
        box-sizing: border-box;
    }

    .b-details__contact-cols--two {
        grid-template-columns: 1fr;
        row-gap: 12px;
    }
}

@media (min-width: 640px) {
    .b-details__services-list {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 18px;
    }
}

@media (min-width: 920px) {
    .b-details__hero {
        height: 300px;
    }

    .b-details__intro {
        margin-top: -64px;
        padding: 20px 24px 10px;
    }

    .b-details__grid {
        grid-template-columns: 1fr minmax(280px, 340px);
        gap: 32px;
    }

    .b-details__grid-main {
        gap: 28px;
    }

    .b-details__services-list {
        gap: 20px;
    }

    .b-image-slide {
        height: 150px;
    }
}

@media (max-width: 919px) {
    .b-details__card--sticky {
        position: static;
    }
}
</style>